import { TypedDocumentNode } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { AccessEvent, MobileOpCode, Query, QueryGetAccessEventsForTenantArgs } from 'graphql/generated/graphqlTypes';
import { GET_ACCESS_EVENTS_FOR_TENANT } from 'graphql/v2/queries';
import { useEffect, useMemo, useState } from 'react';
import { TranslatedHookProps } from 'v2/hooks/hooks.types';

dayjs.extend(utc);

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
// The pages are fetched with `client.query` rather than `useLazyQuery`: the lazy query's observable rejected
// the in-flight request with an `AbortError` while the dashboard was still mounted.
const GET_ACCESS_EVENTS_FOR_TENANT_TYPED: TypedDocumentNode<
  Pick<Query, 'getAccessEventsForTenant'>,
  QueryGetAccessEventsForTenantArgs
> = GET_ACCESS_EVENTS_FOR_TENANT;

export const LAST_WEEK_DAYS = 7;

/** How many devices either of the rankings below shows. */
export const DEVICE_RANKING_LIMIT = 5;

export interface DailyOpeningCount {
  /** Start of the day this bucket covers, as an ISO string — format it with `formatDate`. */
  date: string;
  count: number;
}

export interface DeviceAccessCount {
  deviceId: string;
  deviceName: string;
  count: number;
}

// the window covers LAST_WEEK_DAYS days ending today, so it starts LAST_WEEK_DAYS - 1 days ago
const getLastWeekStartOfDay = () =>
  dayjs()
    .startOf('day')
    .subtract(LAST_WEEK_DAYS - 1, 'day');

// fixed at effect start so every page of the loop below queries the exact same window
const getLastWeekDateRange = () => ({
  rangeStart: getLastWeekStartOfDay().utc().format(),
  rangeEnd: dayjs().endOf('day').utc().format()
});

// highest first, so a chart can render the ranking in data order
const rankDevicesByCount = (events: AccessEvent[]): DeviceAccessCount[] => {
  const countsByDevice = new Map<string, DeviceAccessCount>();

  events.forEach(({ door }) => {
    const device = countsByDevice.get(door.id);

    if (device) {
      device.count += 1;
      return;
    }

    countsByDevice.set(door.id, { deviceId: door.id, deviceName: door.name, count: 1 });
  });

  return (
    Array.from(countsByDevice.values())
      // the name tiebreak keeps devices on an equal count in a stable, predictable order instead of
      // letting the order the events happened to arrive in decide the ranking
      .sort((first, second) => second.count - first.count || first.deviceName.localeCompare(second.deviceName))
      .slice(0, DEVICE_RANKING_LIMIT)
  );
};

const useWeeklyAccessEvents = ({ handleFetchError, translation }: TranslatedHookProps) => {
  const [accessEvents, setAccessEvents] = useState<AccessEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const client = useApolloClient();

  useEffect(() => {
    // Unmounting mid-fetch abandons the paging loop: without this the remaining pages would keep
    // being requested, and their state setters would fire, for data nobody will see.
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(false);

      const dateRange = getLastWeekDateRange();
      const items: AccessEvent[] = [];
      let nextToken: string | null | undefined;

      // no `limit` is passed on purpose, so each page comes back as large as the API allows —
      // that keeps the number of round trips (and therefore the total fetch time) as low as possible
      do {
        let data;
        try {
          ({ data } = await client.query({
            query: GET_ACCESS_EVENTS_FOR_TENANT_TYPED,
            variables: {
              nextToken: nextToken ?? undefined,
              filter: {
                // both actions come back in the one paginated pass: the granted events shape the
                // openings charts, the denied ones the denied ranking, and they cover the same window
                actions: [MobileOpCode.AccessGranted, MobileOpCode.AccessDenied],
                dateRange
              }
            }
          }));
        } catch (error) {
          // a cancelled run must not report an error against a hook that is already gone
          if (cancelled) return;
          handleFetchError(translation.access_events_error_fetching);
          console.error(error);
          setError(true);
          setLoading(false);
          return;
        }

        // checked before anything else so a cancelled run neither requests the next page nor
        // reports an error against a hook that is already gone
        if (cancelled) return;

        if (!data) break;

        items.push(...data.getAccessEventsForTenant.items);
        nextToken = data.getAccessEventsForTenant.nextToken;
      } while (nextToken);

      setAccessEvents(items);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // The API applies the range filter in UTC while everything below buckets by local day, so an event
  // near midnight can come back just outside the window. Filtering once here keeps every projection
  // describing the exact same set of events — otherwise the cards built on them could disagree on
  // how many openings the week contained.
  const eventsInWindow = useMemo(() => {
    const windowStart = getLastWeekStartOfDay();

    return accessEvents.filter(({ createdAt }) => {
      const dayOffset = dayjs(createdAt).startOf('day').diff(windowStart, 'day');

      return dayOffset >= 0 && dayOffset < LAST_WEEK_DAYS;
    });
  }, [accessEvents]);

  // Everything below reads one of these two rather than `eventsInWindow`, so an opening always means
  // a granted event — the denied events the query now also returns never reach the openings charts.
  const grantedInWindow = useMemo(
    () => eventsInWindow.filter(({ action }) => action === MobileOpCode.AccessGranted),
    [eventsInWindow]
  );

  const deniedInWindow = useMemo(
    () => eventsInWindow.filter(({ action }) => action === MobileOpCode.AccessDenied),
    [eventsInWindow]
  );

  // one bucket per day, oldest first, so empty days still render as a zero-height bar
  const dailyOpenings = useMemo<DailyOpeningCount[]>(() => {
    const windowStart = getLastWeekStartOfDay();
    const buckets = Array.from({ length: LAST_WEEK_DAYS }, (_, index) => ({
      date: windowStart.add(index, 'day'),
      count: 0
    }));

    grantedInWindow.forEach(({ createdAt }) => {
      const dayOffset = dayjs(createdAt).startOf('day').diff(windowStart, 'day');

      buckets[dayOffset].count += 1;
    });

    return buckets.map(({ date, count }) => ({ date: date.format(), count }));
  }, [grantedInWindow]);

  const mostAccessedDevices = useMemo(() => rankDevicesByCount(grantedInWindow), [grantedInWindow]);

  const mostDeniedDevices = useMemo(() => rankDevicesByCount(deniedInWindow), [deniedInWindow]);

  const totalOpenings = useMemo(() => dailyOpenings.reduce((sum, { count }) => sum + count, 0), [dailyOpenings]);

  const averageOpeningsPerDay = useMemo(() => Math.round(totalOpenings / LAST_WEEK_DAYS), [totalOpenings]);

  // undefined while there is nothing to show, so the caller can render an empty state instead of a 0 peak
  const peakDay = useMemo<DailyOpeningCount | undefined>(() => {
    if (totalOpenings === 0) return undefined;

    return dailyOpenings.reduce((peak, day) => (day.count > peak.count ? day : peak));
  }, [dailyOpenings, totalOpenings]);

  return {
    dailyOpenings,
    mostAccessedDevices,
    mostDeniedDevices,
    totalOpenings,
    averageOpeningsPerDay,
    peakDay,
    loading,
    error
  };
};

export default useWeeklyAccessEvents;
